/**
 * Browser-compatible crypto utilities for WhatsApp message encryption
 *
 * Mirrors the server-side implementation in supabase/functions/_shared/crypto.ts
 * Uses Web Crypto API available in modern browsers
 */

/**
 * Chiavi AES già derivate, per materiale di partenza (org + salt).
 *
 * La derivazione PBKDF2 a 100.000 iterazioni costa decine di millisecondi: rifarla per
 * OGNI messaggio rendeva il costo lineare nel numero di messaggi (centinaia di messaggi =
 * decine di secondi di main thread bloccato). La chiave è deterministica per la stessa
 * organizzazione, quindi si deriva una volta sola e si riusa. Si memorizza la Promise,
 * non la chiave: decrypt concorrenti partiti insieme condividono la stessa derivazione.
 */
const derivedKeyCache = new Map<string, Promise<CryptoKey>>();

function getDecryptionKey(keyMaterialString: string): Promise<CryptoKey> {
  const cached = derivedKeyCache.get(keyMaterialString);
  if (cached) return cached;

  const derivation = (async () => {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(keyMaterialString),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    const salt = encoder.encode('crmobly-whatsapp-salt-v1'); // Stesso salt del server

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );
  })();

  // Una derivazione fallita non resta in cache: il tentativo successivo riprova.
  derivation.catch(() => derivedKeyCache.delete(keyMaterialString));
  derivedKeyCache.set(keyMaterialString, derivation);
  return derivation;
}

/**
 * Decifra un messaggio WhatsApp cifrato con AES-256-GCM
 *
 * @param encryptedBase64 - Base64 string contenente IV + ciphertext + auth tag
 * @param organizationId - ID organizzazione per derivare chiave univoca
 * @returns Testo in chiaro, o stringa originale se decryption fallisce
 */
export async function decryptWhatsAppMessage(
  encryptedBase64: string,
  organizationId?: string
): Promise<string> {
  if (!encryptedBase64) return encryptedBase64;

  try {
    // Ottieni salt da env (disponibile in Vite con VITE_ prefix)
    const baseSalt = import.meta.env.VITE_ENCRYPTION_SALT || 'crmobly-default-salt';
    const key = organizationId ? `${organizationId}-${baseSalt}` : baseSalt;

    const cryptoKey = await getDecryptionKey(key);

    // Decodifica da base64
    const combined = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0));

    // Estrai IV (primi 12 bytes) e ciphertext (resto)
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);

    // Decifra con AES-GCM
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, ciphertext);

    return new TextDecoder().decode(plaintext);
  } catch (error) {
    console.error('[Crypto] Decryption failed:', error);
    // Fallback: ritorna stringa originale (potrebbe essere già in chiaro o non cifrata)
    return encryptedBase64;
  }
}

/**
 * Verifica se una stringa sembra essere cifrata (base64 valido con lunghezza minima)
 */
export function isEncrypted(text: string): boolean {
  if (!text || text.length < 16) return false;

  // Verifica se sembra base64
  const base64Regex = /^[A-Za-z0-9+/]+=*$/;
  return base64Regex.test(text);
}
